import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { EVENT } from "../core/keys"
import {
  type RuntimeState as BaseRuntimeState,
  type RuntimeStateContext as BaseRuntimeStateContext,
  StatefulRuntime as BaseStatefulRuntime,
} from "../core/stateful-runtime"
import { THEME } from "../core/theme"
import { SPRITE_SIZES } from "../data/constants"
import type { GameSprite, Player } from "../types"
import type { VfxManager } from "./vfx-manager"

const COLOR = THEME.colors

function applyHeroBaseTint(
  sprite: GameSprite,
  powerMode: Player["powerMode"],
): void {
  if (powerMode === "overdrive") {
    sprite.setTint(COLOR.magenta)
  } else {
    sprite.clearTint()
  }
}

export type HeroMovementState = "normal" | "overdrive" | "launching"
export type HeroPowerState = "idle" | "overdrive"
export type HeroInvulState = "vulnerable" | "invulnerable"

export interface HeroStateContext extends BaseRuntimeStateContext {
  player: Player
  sprite: GameSprite
  vfx: VfxManager
  onDestroyThreatsTouchingPlayer: () => void
}

export class HeroMovementRuntime extends BaseStatefulRuntime<
  HeroMovementState,
  HeroStateContext
> {
  private _player: Player | null = null
  private _sprite: GameSprite | null = null
  private _vfx: VfxManager | null = null
  private _overrideState: HeroMovementState | null = null

  constructor(initial: HeroMovementState) {
    const states: BaseRuntimeState<HeroMovementState, HeroStateContext>[] = [
      { name: "normal", update: () => null },
      { name: "overdrive", update: () => null },
      { name: "launching", update: () => null },
    ]
    super(initial, states, (time, delta, _stateName, stateTime) => ({
      time,
      delta,
      stateTime,
      player: this._player!,
      sprite: this._sprite!,
      vfx: this._vfx!,
      onDestroyThreatsTouchingPlayer: null as any,
    }))
  }

  public run(
    time: number,
    delta: number,
    player: Player,
    sprite: GameSprite,
    vfx: VfxManager,
  ): void {
    this._player = player
    this._sprite = sprite
    this._vfx = vfx
    const nextState: HeroMovementState =
      this._overrideState ??
      (player.powerMode === "overdrive" ? "overdrive" : "normal")
    if (this.getStateName() !== nextState) {
      this.forceTransition(nextState)
    }
    this.tick(time, delta)
  }

  public setOverrideState(state: HeroMovementState | null): void {
    this._overrideState = state
  }

  protected override onEntered(
    state: HeroMovementState,
    ctx: HeroStateContext,
  ): void {
    ctx.sprite.setData("heroMovementState", state)
    EventBus.emit(EVENT.HERO_STATE_CHANGED, state)
  }
}

export class HeroPowerRuntime extends BaseStatefulRuntime<
  HeroPowerState,
  HeroStateContext
> {
  private _player: Player | null = null
  private _sprite: GameSprite | null = null
  private _vfx: VfxManager | null = null
  private _onDestroyThreatsTouchingPlayer: (() => void) | null = null
  private _requestedState: HeroPowerState = "idle"
  private _requestedDuration = 0
  private _hasPendingRequest = false

  constructor() {
    const states: BaseRuntimeState<HeroPowerState, HeroStateContext>[] = [
      {
        name: "idle",
        enter: (ctx) => {
          ctx.player.powerMode = null
          ctx.player.powerTimer = 0
          if (!ctx.player.isInvulnerable) {
            applyHeroBaseTint(ctx.sprite, ctx.player.powerMode)
          }
        },
        update: (ctx) => {
          const now = ctx.time
          const hoverPulseX = 1 + Math.sin(now / 180) * 0.03
          const hoverPulseY = 1 - Math.sin(now / 180) * 0.03
          ctx.sprite.setDisplaySize(
            SPRITE_SIZES.player_sprite.w * hoverPulseX,
            SPRITE_SIZES.player_sprite.h * hoverPulseY,
          )
          return null
        },
      },
      {
        name: "overdrive",
        enter: (ctx) => {
          ctx.player.powerMode = "overdrive"
          if (!ctx.player.isInvulnerable) {
            applyHeroBaseTint(ctx.sprite, ctx.player.powerMode)
          }
          Phaser.Actions.AddEffectShine(ctx.sprite, {
            duration: 600,
            direction: 0,
            colorFactor: [2.0, 2.0, 0.4, 1],
          })
          ctx.vfx.heatHazePulse(0.015, 800)
        },
        update: (ctx) => ((ctx.player.powerTimer ?? 0) <= 0 ? "idle" : null),
      },
    ]
    super("idle", states, (time, delta, _stateName, stateTime) => ({
      time,
      delta,
      stateTime,
      player: this._player!,
      sprite: this._sprite!,
      vfx: this._vfx!,
      onDestroyThreatsTouchingPlayer: this._onDestroyThreatsTouchingPlayer!,
    }))
  }

  public run(
    time: number,
    delta: number,
    player: Player,
    sprite: GameSprite,
    vfx: VfxManager,
    onDestroyThreats: () => void,
  ): void {
    this._player = player
    this._sprite = sprite
    this._vfx = vfx
    this._onDestroyThreatsTouchingPlayer = onDestroyThreats
    if (this._hasPendingRequest) {
      this.applyRequestedState()
    }
    this.tick(time, delta)
  }

  public activate(
    mode: Exclude<HeroPowerState, "idle">,
    duration: number,
  ): void {
    this._requestedState = mode
    this._requestedDuration = duration
    this._hasPendingRequest = true
    this.applyRequestedState()
  }

  public deactivate(): void {
    this._requestedState = "idle"
    this._requestedDuration = 0
    this._hasPendingRequest = true
    this.applyRequestedState()
  }

  private applyRequestedState(): void {
    if (!this._player || !this._hasPendingRequest) return

    if (this._requestedState === "idle") {
      this._player.powerTimer = 0
      if (this.getStateName() !== "idle") {
        this.forceTransition("idle")
      }
      this._hasPendingRequest = false
      return
    }

    this._player.powerTimer = this._requestedDuration
    if (this.getStateName() !== this._requestedState) {
      this.forceTransition(this._requestedState)
    }
    this._hasPendingRequest = false
  }

  protected override onEntered(
    state: HeroPowerState,
    ctx: HeroStateContext,
  ): void {
    const nextMode = state === "idle" ? null : state
    ctx.sprite.setData("powerMode", nextMode)
    EventBus.emit(EVENT.POWER_MODE_CHANGED, nextMode)
  }
}

export class HeroInvulRuntime extends BaseStatefulRuntime<
  HeroInvulState,
  HeroStateContext
> {
  private _player: Player | null = null
  private _sprite: GameSprite | null = null
  private _vfx: VfxManager | null = null

  constructor() {
    const states: BaseRuntimeState<HeroInvulState, HeroStateContext>[] = [
      {
        name: "vulnerable",
        enter: (ctx) => {
          ctx.player.isInvulnerable = false
          applyHeroBaseTint(ctx.sprite, ctx.player.powerMode)
          ctx.sprite.setAlpha(1)
        },
        update: (ctx) => (ctx.player.isInvulnerable ? "invulnerable" : null),
      },
      {
        name: "invulnerable",
        enter: (ctx) => {
          ctx.player.isInvulnerable = true
        },
        update: (ctx) => {
          if (!ctx.player.isInvulnerable) return "vulnerable"

          const flash = Math.sin(ctx.time / 40) > 0
          ctx.sprite.setAlpha(flash ? 0.3 : 1)
          if (flash) {
            ctx.sprite.setTint(COLOR.dangerBright)
          } else {
            applyHeroBaseTint(ctx.sprite, ctx.player.powerMode)
          }

          return null
        },
        exit: (ctx) => {
          applyHeroBaseTint(ctx.sprite, ctx.player.powerMode)
          ctx.sprite.setAlpha(1)
        },
      },
    ]
    super("vulnerable", states, (time, delta, _stateName, stateTime) => ({
      time,
      delta,
      stateTime,
      player: this._player!,
      sprite: this._sprite!,
      vfx: this._vfx!,
      onDestroyThreatsTouchingPlayer: null as any,
    }))
  }

  public run(
    time: number,
    delta: number,
    player: Player,
    sprite: GameSprite,
    vfx: VfxManager,
  ): void {
    this._player = player
    this._sprite = sprite
    this._vfx = vfx
    if (player.isInvulnerable && (player.invulnerabilityTimer ?? 0) > 0) {
      player.invulnerabilityTimer = Math.max(
        0,
        (player.invulnerabilityTimer ?? 0) - delta,
      )
      if (player.invulnerabilityTimer <= 0) {
        player.isInvulnerable = false
      }
    }
    this.tick(time, delta)
  }
}
